import type React from "react";
import type { NavLinkProps } from "react-router";
import { NavLink, useLocation } from "react-router";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";

import { cn } from "~/lib/ui";

const NavigationMenu = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Root>) => (
  <NavigationMenuPrimitive.Root
    ref={ref}
    className={cn(
      "relative z-10 flex max-w-max flex-1 items-center justify-center",
      className,
    )}
    {...props}
  >
    {children}
  </NavigationMenuPrimitive.Root>
);
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;

const NavigationMenuList = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.List>) => (
  <NavigationMenuPrimitive.List
    ref={ref}
    className={cn(
      "group flex flex-1 list-none items-center justify-center space-x-1",
      className,
    )}
    {...props}
  />
);
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;

const NavigationMenuItem = NavigationMenuPrimitive.Item;

const navigationMenuTriggerStyle = tv({
  base: "group inline-flex h-9 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50",
});

const NavigationMenuTrigger = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Trigger>) => (
  <NavigationMenuPrimitive.Trigger
    ref={ref}
    className={cn(navigationMenuTriggerStyle(), "group", className)}
    {...props}
  >
    {children}
  </NavigationMenuPrimitive.Trigger>
);
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;

const NavigationMenuContent = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Content>) => (
  <NavigationMenuPrimitive.Content
    ref={ref}
    className={cn(
      "left-0 top-0 w-full data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 md:absolute md:w-auto",
      className,
    )}
    {...props}
  />
);
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;

const NavigationMenuLink = ({
  className,
  children,
  prefetch,
  isMainMenu,
  ref,
  ...props
}: Omit<NavLinkProps, "className"> & {
  className?: string;
  isMainMenu?: boolean;
} & { ref?: React.Ref<HTMLAnchorElement> }) => {
  const href = useLocation().pathname;
  const isActive = href === props.to;
  const underlineActiveItem = isMainMenu && isActive;

  return (
    <NavLink
      ref={ref}
      prefetch={prefetch ?? "render"}
      className={() =>
        cn(
          "relative w-auto data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52",
          className,
          underlineActiveItem && "underline underline-offset-4",
        )
      }
      {...props}
    >
      {children}
    </NavLink>
  );
};
NavigationMenuLink.displayName = "NavigationMenuLink";

const ViewPortVariants = tv({
  slots: {
    container: "absolute left-0 top-full flex justify-center -shadow",
    viewport:
      "relative h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-md border bg-popover text-popover-foreground md:w-[var(--radix-navigation-menu-viewport-width)] ",
  },
  variants: {
    shadow: {
      true: {
        viewport: "shadow",
      },
    },
    background: {
      transparent: {
        container: "bg-transparent",
        viewport: "bg-transparent",
      },
      contrast: {
        container: "bg-panel-translucent",
      },
    },
    placement: {
      wideCenter: {
        container: " w-[100%] -border",
        viewport: " -border bg-panel-translucent",
      },
      center: {},
    },
    animation: {
      none: {
        viewport:
          "h-0 data-[state=open]:h-[var(--radix-navigation-menu-viewport-height)]",
      },
      zoomIn: {
        viewport:
          "origin-top-center  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90",
      },

      heightReveal: {
        viewport:
          "origin-top-center h-0 data-[state=open]:h-[var(--radix-navigation-menu-viewport-height)] transition-[height] ease-in-out",
      },
    },
  },
  defaultVariants: {
    animation: "heightReveal",
    placement: "wideCenter",
    shadow: false,
    background: "contrast",
  },
});
export type ViewPortVariantsType = VariantProps<typeof ViewPortVariants>;

const NavigationMenuViewport = ({
  className,
  contailerClassName,
  ref,
  ...props
}: {
  contailerClassName?: string;
} & ViewPortVariantsType &
  React.ComponentProps<typeof NavigationMenuPrimitive.Viewport>) => {
  const { container, viewport } = ViewPortVariants({
    ...props,
  });
  return (
    <div className={container({ className: contailerClassName })}>
      <NavigationMenuPrimitive.Viewport
        className={viewport({ className })}
        ref={ref}
        {...props}
      />
    </div>
  );
};
NavigationMenuViewport.displayName =
  NavigationMenuPrimitive.Viewport.displayName;

const NavigationMenuIndicator = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Indicator>) => (
  <NavigationMenuPrimitive.Indicator
    ref={ref}
    className={cn(
      "top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden data-[state=visible]:animate-in data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in",
      className,
    )}
    {...props}
  >
    <div className="relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-border shadow-md" />
  </NavigationMenuPrimitive.Indicator>
);
NavigationMenuIndicator.displayName =
  NavigationMenuPrimitive.Indicator.displayName;

export {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  navigationMenuTriggerStyle,
};
